import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useState} from 'react';
import AurButton from '../../../common/aur-button/AurButton';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurPopup from '../../../common/aur-popup/AurPopup';
import AurBanner from '../../../common/aur-banner/AurBanner';
import {MAX_CHAR_LIMIT} from '../../../../constants/constants';
import AurSvgIcon from '../../../common/aur-svg-icon/AurSvgIcon';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {ReactComponent as PlusIcon} from '../../../../assests/svg/plus-icon.svg';
import {useAddNewTag} from '../../../../hooks/eca-tagging/useAddNewTag';
import {AurLoadingButton} from '../../../common/aur-loading-button/AurLoadingButton';
import {IFieldModal} from '../../../pages/field-management/custom-field/constant';
import {ITag} from '../../../pages/field-management/custom-field/DraggableTag';
import {isValidTagName} from '../../../../utils/utils';

interface AddNewTagProps {
  open: boolean;
  onClose: (tag?: ITag) => void;
  selectedTagField: IFieldModal;
  displayOrder: number;
}

const errorIcon = (
  <AurIcon icon={IconNames.Error} color={IconColors.Critical} />
);

const AddNewTag = ({
  open,
  onClose,
  selectedTagField,
  displayOrder,
}: AddNewTagProps) => {
  const [isTagNameError, setIsTagNameError] = useState(false);
  const [tagName, setTagName] = useState<string>('');
  const [openDuplicateTagModal, setopenDuplicateTagModal] = useState(false);
  const {addTag, isLoading} = useAddNewTag();

  const validateName = useCallback(
    (name: string): boolean => {
      const isValid = isValidTagName(name);
      setIsTagNameError(!isValid);
      return isValid;
    },
    [setIsTagNameError]
  );

  const handleTagNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTagName(value);
    setIsTagNameError(false);
  };

  const handleOk = useCallback(() => {
    setTagName('');
    setopenDuplicateTagModal(false);
  }, []);

  const handleSaveAndApplyClick = () => {
    if (!validateName(tagName)) {
      return;
    }

    const isDuplicate = selectedTagField?.tagValues?.some(
      (tag) => tag.tagValue.toLowerCase() === tagName.toLowerCase()
    );

    if (isDuplicate) {
      setopenDuplicateTagModal(true);
      return;
    }

    const onTagSuccess = (newTagId: string) => {
      onClose({
        tagValueId: Number(newTagId),
        tagValue: tagName,
        displayOrder,
      });
    };

    addTag(
      {value: tagName, displayOrder},
      selectedTagField.fieldId.toString(),
      onTagSuccess
    );
  };

  if (!open) return null; // Hide when `open` is false

  return (
    <Box className="add-tag-container">
      <Box>
        <Typography
          className={`typo-text subhead3_size subhead3_size_cf subhead3-text-color`}
        >
          {t('ecaDocList.bulkTagging.createTag.createNewTagIn')}
        </Typography>
        <Typography className={`typo-text body2_size`}>
          {selectedTagField?.fieldLabel}
        </Typography>
      </Box>

      {/* Scrollable Content */}
      {isTagNameError && (
        <AurBanner
          errorText={t('ecaDocList.bulkTagging.addNewTag.tagNameError')}
          errorType="error"
          showIcon={true}
        />
      )}

      <AurTextField
        label={t('ecaDocList.bulkTagging.createTag.tagName')}
        name="create-new-tag"
        value={tagName}
        onChange={handleTagNameChange}
        InputProps={{
          endAdornment: isTagNameError ? (
            <AurSvgIcon src={'../assests/images/info.svg'} />
          ) : null,
        }}
        variant="filled"
        fullWidth
        ariaLabel={t('ecaDocList.bulkTagging.createTag.tagName')}
        className="aur-textfield custom-text-field textfield add-tag-textfield"
        helperText={t('ecaDocList.bulkTagging.createTag.tagCharValidation')}
        error={!!isTagNameError}
        inputProps={{
          maxLength: MAX_CHAR_LIMIT,
        }}
      />

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" mt={2}>
        <AurButton
          type="continue"
          label={t('buttons.cancel')}
          aria-live="polite"
          size="Small"
          dataTestId="cancel-new-tag"
          onClick={() => onClose()}
        />

        <AurLoadingButton
          label={t('ecaDocList.bulkTagging.createTag.saveAndApplyTag')}
          loading={isLoading}
          onClick={handleSaveAndApplyClick}
          size="Small"
          buttonType="submit"
          classNames={`typo-text ${isLoading && 'apply-button-loading'}`}
          disabled={tagName.length === 0 || isTagNameError}
          startIcon={!isLoading && <PlusIcon aria-hidden="true" />}
          dataTestId="create-new-tag"
        />
      </Box>
      {openDuplicateTagModal && (
        <AurPopup
          className="aur-unsaved-changes-popup duplicate-tag-confirmation-popup"
          open={openDuplicateTagModal}
          onOk={handleOk}
          onCancel={handleOk}
          popupHeading={t('ecaDocList.bulkTagging.createTag.popupHeading')}
          showPopupHeading={true}
          popupContent={t(
            'ecaDocList.bulkTagging.createTag.duplicateTagSubheading'
          )}
          labelActionOk={t('ecaDocList.bulkTagging.addNewTag.okay')}
          headingIcon={errorIcon}
          showCancelOption={false}
        />
      )}
    </Box>
  );
};

export default AddNewTag;
